import { useCallback, useEffect, useState } from "react";

import Script from "next/script";

import { useMutation } from "@tanstack/react-query";

import { AmplitudeApi } from "@/api/amplitude";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { useLocalLadyIdContext } from "@/context/local-lady-id-context/LocalLadyId.context";
import { useSendSignInForm } from "@/features/forms/sign-in-form/hooks";
import { InfoModalResDto } from "@/models/infoModalRes.dto";

import { GoogleApi } from "./api/Google.api";
import { helpers } from "./helpers";

declare global {
  interface Window {
    google: TODO;
  }
}

type GoogleInitCBParams = {
  credential: string;
};

export const useGoogleInit = () => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [gmail, setGmail] = useState<string | null>(null);
  const [googleResponse, setGoogleResponse] =
    useState<GoogleInitCBParams | null>(null);
  const bindGoogleButton = useCallback(
    (element: HTMLElement | null) => {
      if (!sdkLoaded) return;

      window.google.accounts.id.renderButton(
        element,
        { theme: "outline", size: "large", type: "icon", shape: "circle" }, // customization attributes
      );
    },
    [sdkLoaded],
  );

  useEffect(() => {
    if (!sdkLoaded) return;
    window.google.accounts.id.initialize({
      client_id: process.env.GOOGLE_CLIENT_ID,
      ux_mode: "popup",
      callback: (res: GoogleInitCBParams) => {
        const gmail = helpers.getEmailFromGoogleCreds(res.credential);

        setGmail(gmail);
        setGoogleResponse(res);
      },
    });
  }, [bindGoogleButton, sdkLoaded]);

  const renderScripts = useCallback(() => {
    return (
      <>
        <Script
          async
          src="https://accounts.google.com/gsi/client"
          onLoad={() => {
            setSdkLoaded(true);
          }}
          onReady={() => {
            setSdkLoaded(true);
          }}
        />
      </>
    );
  }, []);

  const { mutate: loginWithGoogle } = useLoginWithGoogle(gmail);

  useEffect(() => {
    if (!gmail || !googleResponse) return;
    loginWithGoogle({ googleResponse });
  }, [gmail, googleResponse, loginWithGoogle]);

  return { renderScripts, bindGoogleButton };
};

const useLoginWithGoogle = (gmail: string | null) => {
  const { openModal } = useOpenModal();
  const openInformationModal = (dto: InfoModalResDto) => {
    openModal("INFORMATION_MODAL", { ...dto });
  };

  const ladyLocalId = useLocalLadyIdContext();

  const { mutate: sendSignIn } = useSendSignInForm({
    isLoginFromGoogleLogin: true,
    ladyId: ladyLocalId,
  });
  const { mutate: sendSignInFromRegister } = useSendSignInForm({
    isLoginFromRegistration: true,
    ladyId: ladyLocalId,
  });

  return useMutation(GoogleApi.loginWithGoogleCreds, {
    onSuccess: async (data) => {
      if (!data) {
        return;
      }

      if (helpers.isError(data)) {
        openInformationModal({ statusReg: "fail", message: data.ex });

        await AmplitudeApi.send({
          amplitude: "sign_up",
          signup_method: "google",
          error: true,
        });

        return;
      }

      if (!gmail) {
        openInformationModal({
          statusReg: "fail",
          message: "Error with getting google information",
        });

        await AmplitudeApi.send({
          amplitude: "sign_up",
          signup_method: "google",
          error: true,
        });

        return;
      }

      if (helpers.isSuccessLogin(data)) {
        sendSignIn({
          username: gmail,
          userpass: data.info,
        });
        return;
      }

      if (helpers.isSuccessRegister(data)) {
        await AmplitudeApi.send({
          amplitude: "sign_up",
          signup_method: "google",
          error: false,
        });

        sendSignInFromRegister({
          username: gmail,
          userpass: data.info,
        });
        return;
      }
    },
  });
};
